/**
 * Detail card for a single placement.
 *
 * Opens on click from the Kanban card or table row and shows:
 *   - the blogger (avatar + name + platform + handle + categories + base CPV/reach)
 *   - the placement itself (status, publish date, price, last message)
 *   - the creative that should run on this channel, rendered as a Telegram
 *     post via the shared <TelegramPost>. Looks up the variant either in
 *     the campaign's user-saved variants (creative_payload.variants) or,
 *     when not assigned, shows an empty state with a hint to set it from
 *     the wizard / "Креативы" drawer.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, Check, Copy, ExternalLink, MessageSquare, RefreshCw, Sparkles, TrendingUp, Wallet, X } from "lucide-react";
import { useBloggers, useCampaignCreatives, type Blogger, type Placement } from "@/shared/api/wb";
import { Avatar, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRubFull } from "@/shared/lib/format";
import { TelegramPost } from "../CampaignWizard/TelegramPost";
import { UtmBlock } from "./UtmBlock";
import { WriteToModal } from "@/pages/WriteToModal";

export function PlacementDetailModal({
  placement, campaignId, statusLabel, onClose, onReplace, onRepeat, onFindSimilar, onConfirm,
  onApproveScript, onRequestScriptChanges, onCancel,
}: {
  placement: Placement; campaignId: string; statusLabel: string;
  onClose: () => void;
  onReplace?: () => void;
  onRepeat?: () => void;
  onFindSimilar?: () => void;
  onConfirm?: () => void;
  onApproveScript?: () => void;
  onRequestScriptChanges?: () => void;
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const [inboxOpen, setInboxOpen] = useState(false);
  const isDone = placement.status === "done" || placement.status === "published";
  const { data: bloggers } = useBloggers({});
  const { data: creativesData } = useCampaignCreatives(campaignId);
  const blogger = (bloggers ?? []).find((b) => b.id === placement.blogger_id);
  const variants = creativesData?.variants ?? [];
  // Prefer the explicitly assigned variant; fall back to the first one if a
  // creative_id points at a global Creative we don't have locally.
  const assigned = variants.find((v) => v.id === placement.creative_id) ?? variants[0];

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg fade-up flex flex-col overflow-hidden"
        style={{ width: "min(880px, 94vw)", height: "min(88vh, 860px)" }}>
        <div className="flex items-center justify-between p-6 border-b border-ink-12 flex-shrink-0">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">Размещение</div>
            <h3 className="font-display font-bold text-[20px] tracking-tight">{blogger?.name ?? placement.blogger_id}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInboxOpen(true)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
            >
              <MessageSquare size={13} /> Написать
            </button>
            {onCancel && (
              <button
                onClick={() => { onClose(); onCancel(); }}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-40 hover:text-danger hover:border-danger/40 transition-colors"
              >
                <X size={13} /> Отменить
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 p-6 grid grid-cols-[1fr_320px] gap-6 items-start">
          <div className="grid gap-5">
            {blogger && <BloggerBlock blogger={blogger} />}
            <PlacementBlock placement={placement} statusLabel={statusLabel} />
            <UtmBlock placement={placement} campaignId={campaignId} blogger={blogger} />
            <CreativeBlock variant={assigned} trackedLink={placement.utm_link} />
          </div>

          {/* Side rail with the most important numbers, for scanability */}
          <aside className="grid gap-3 self-start">
            <SideStat label="Цена размещения" value={fmtRubFull(placement.price)} icon={<Wallet size={14} />} />
            <SideStat label="Дата выхода" value={placement.publish_date} icon={<Calendar size={14} />} />
            <SideStat label="Статус" value={statusLabel} />
            {placement.is_overdue && (
              <div className="p-3 rounded-md bg-warning/10 border border-warning/30 text-[12.5px] font-display font-semibold text-ink">
                Просрочено согласование сценария
              </div>
            )}
            {placement.result && (
              <>
                <SideStat label="Просмотры" value={fmtNum(placement.result.views)} />
                <SideStat label="CPV" value={`${placement.result.cpv}₽`} accent={placement.result.cpv < 0.5} />
                <SideStat label="Клики" value={fmtNum(placement.result.clicks)} />
                <SideStat label="Конверсии" value={String(placement.result.conv)} />
              </>
            )}
          </aside>
        </div>

        {/* Action footer for done/published placements */}
        {isDone && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-ink-12 flex-shrink-0"
            style={{ background: "rgba(191,249,63,0.10)" }}>
            <TrendingUp size={15} className="text-success flex-shrink-0" />
            <span className="text-[13px] font-display font-semibold flex-1 text-success">
              Размещение завершено
            </span>
            {onFindSimilar && (
              <button
                onClick={() => { onClose(); onFindSimilar(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink"
              >
                <Sparkles size={14} /> Похожие каналы
              </button>
            )}
            {onRepeat && (
              <button
                onClick={() => { onClose(); onRepeat(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-ink text-white text-[12.5px] font-display font-semibold hover:bg-[#2A2A2A]"
              >
                <Copy size={14} /> Повторить размещение
              </button>
            )}
          </div>
        )}

        {/* Action footer for a submitted native script awaiting approval */}
        {placement.status === "script_review" && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-ink-12 bg-cream flex-shrink-0">
            <AlertCircle size={15} className="text-ink-60 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-display font-semibold">Сценарий на согласовании</div>
              {placement.script_draft && (
                <div className="text-[12px] text-ink-60 line-clamp-2">{placement.script_draft}</div>
              )}
            </div>
            {onRequestScriptChanges && (
              <button
                onClick={() => { onClose(); onRequestScriptChanges(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink"
              >
                <MessageSquare size={14} /> На доработку
              </button>
            )}
            {onApproveScript && (
              <button
                onClick={() => { onClose(); onApproveScript(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[12.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
              >
                <Check size={14} strokeWidth={3} /> Согласовать
              </button>
            )}
          </div>
        )}

        {/* Action footer for a placement the advertiser cancelled outright */}
        {placement.status === "cancelled" && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-ink-12 bg-ink-6/50 flex-shrink-0">
            <AlertCircle size={15} className="text-ink-60 flex-shrink-0" />
            <span className="text-[13px] text-ink-60 font-display font-semibold flex-1">
              {placement.cancel_reason || "Отменено рекламодателем"}
            </span>
          </div>
        )}

        {/* Action footer for a submitted report awaiting advertiser confirmation */}
        {placement.status === "review" && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-ink-12 bg-cream flex-shrink-0">
            <AlertCircle size={15} className="text-ink-60 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-display font-semibold">Отчёт на проверке</div>
              {placement.report_link && (
                <a href={placement.report_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] text-link hover:underline truncate">
                  <ExternalLink size={11} /> {placement.report_link}
                </a>
              )}
            </div>
            {onConfirm && (
              <button
                onClick={() => { onClose(); onConfirm(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[12.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
              >
                <Check size={14} strokeWidth={3} /> Подтвердить и оплатить
              </button>
            )}
          </div>
        )}

        {/* Action footer for declined placements */}
        {placement.status === "declined" && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-ink-12 bg-danger/5 flex-shrink-0">
            <AlertCircle size={15} className="text-danger flex-shrink-0" />
            <span className="text-[13px] text-danger font-display font-semibold flex-1">
              Канал отказался от размещения
            </span>
            <button
              onClick={() => setInboxOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink"
            >
              <MessageSquare size={14} /> Связаться
            </button>
            {onReplace && (
              <button
                onClick={() => { onClose(); onReplace(); }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-ink text-white text-[12.5px] font-display font-semibold hover:bg-[#2A2A2A]"
              >
                <RefreshCw size={14} /> Заменить канал
              </button>
            )}
          </div>
        )}
      </div>

      {inboxOpen && blogger && (
        <WriteToModal
          open
          onClose={() => setInboxOpen(false)}
          campaignId={campaignId}
          blogger={blogger}
        />
      )}
    </div>
  );
}

function BloggerBlock({ blogger }: { blogger: Blogger }) {
  return (
    <section className="grid gap-3">
      <SectionLabel>Блогер</SectionLabel>
      <div className="flex items-center gap-4 p-4 bg-white rounded-md border border-ink-12">
        <Avatar name={blogger.full} size={56} grad={blogger.grad as [string, string]} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 font-display font-bold text-[16px]">
            <PlatformIcon platform={blogger.platform as Platform} size={14} />
            {blogger.name}
            {blogger.tag && (
              <span className="text-[10px] font-mono font-bold bg-lime text-[var(--fg-on-accent)] px-1.5 py-0.5 rounded-sm">{blogger.tag}</span>
            )}
          </div>
          <div className="text-[12.5px] text-ink-40 truncate">{blogger.full}</div>
          <div className="text-[12px] text-ink-60 mt-0.5">{blogger.cats.join(" · ")}</div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-right">
          <MiniStat label="охват" value={fmtNum(blogger.reach)} />
          <MiniStat label="базовый CPV" value={`${blogger.cpv}₽`} />
          <MiniStat label="ER" value={`${blogger.er}%`} />
        </div>
      </div>
    </section>
  );
}

function PlacementBlock({ placement, statusLabel }: { placement: Placement; statusLabel: string }) {
  return (
    <section className="grid gap-3">
      <SectionLabel>Параметры размещения</SectionLabel>
      <div className="grid grid-cols-3 gap-2 p-4 bg-white rounded-md border border-ink-12">
        <KV k="Статус" v={statusLabel} />
        <KV k="Дата выхода" v={placement.publish_date} />
        <KV k="Цена" v={fmtRubFull(placement.price)} />
      </div>
      {placement.msg && (
        <div className="flex gap-2.5 p-3.5 bg-cream rounded-md text-[13px] text-ink-60 leading-relaxed">
          <MessageSquare size={14} className="text-ink-40 flex-shrink-0 mt-0.5" />
          <div>{placement.msg}</div>
        </div>
      )}
      {placement.decline_reason && (
        <div className="px-3.5 py-2.5 rounded-md bg-danger/10 text-danger text-[12.5px]">
          Отказ: {placement.decline_reason}
        </div>
      )}
    </section>
  );
}

function CreativeBlock({ variant, trackedLink }: {
  variant?: { id: string; name: string; text: string; link: string; media: { id: string; kind: "image" | "video"; url: string }[] };
  trackedLink?: string | null;
}) {
  return (
    <section className="grid gap-3">
      <SectionLabel>Объявление</SectionLabel>
      {variant ? (
        <div className="grid gap-2">
          <div className="text-[12px] text-ink-40 px-1">Будет опубликовано в канале:</div>
          <TelegramPost p={{ name: variant.name, text: variant.text, media: variant.media, link: trackedLink || variant.link }} />
        </div>
      ) : (
        <div className="p-6 bg-white rounded-md border border-dashed border-ink-12 text-center text-ink-40">
          <div className="font-display font-semibold text-ink-60 mb-1">Креатив ещё не назначен</div>
          <div className="text-[12.5px]">Создайте вариант через «Креативы в кампании» и привяжите его к этому каналу.</div>
        </div>
      )}
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">{children}</div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono font-bold text-[14px] tabular-nums">{value}</div>
      <div className="text-[10.5px] text-ink-40 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-ink-40">{k}</div>
      <div className="font-display font-bold text-[14px] mt-0.5">{v}</div>
    </div>
  );
}

function SideStat({ label, value, icon, accent }: { label: string; value: string; icon?: React.ReactNode; accent?: boolean }) {
  return (
    <div className="p-3 rounded-md bg-white border border-ink-12">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-ink-40 font-display font-semibold">
        {icon} {label}
      </div>
      <div className={`font-display font-extrabold text-[18px] mt-0.5 tabular-nums ${accent ? "text-success" : ""}`}>{value}</div>
    </div>
  );
}
